import { createBinaryFilter, globalOtsuThreshold, Image4Channel, SquareMatrix } from 'preprocessor';
import { computeTransformationMatrix, generateSampler, locate, readFormatInformation, readVersion } from 'decoder';
import { standardDecodingPipeline2 } from 'jsqr2'

export class QRDecoderDebugger {


  /** @type {HTMLCanvasElement | import('canvas').Canvas} */
  #greyscaleCanvasElement;

  /** @type {HTMLCanvasElement | import('canvas').Canvas} */
  #binaryCanvasElement;

  /** @type {HTMLCanvasElement | import('canvas').Canvas} */
  #adaptiveThresholdingCanvas;
  
  /** @type {boolean} */
  #showLocations

  /** @type {boolean} */
  #showProjectionLines;

  /** @type {boolean} */
  #tryDecode;

  constructor(){

    this.#greyscaleCanvasElement = null;
    this.#binaryCanvasElement = null;
    this.#showLocations = false;
    this.#showProjectionLines = false;
    this.#tryDecode = false;
    this.#adaptiveThresholdingCanvas = null;
  }

  /** 
    * @param {HTMLCanvasElement | import('canvas').Canvas} canvas
    */
  withGreyScaleCanvas(canvas){
    this.#greyscaleCanvasElement = canvas;
    return this;
  }

  /**
    * @param {HTMLCanvasElement | import('canvas').Canvas} canvas
    */
  withBinaryImage(canvas){
    this.#binaryCanvasElement = canvas;
    return this;
  }

  /** 
    * to set whether to enable matrix transfomation line drawing
    */
  showLocations(){
    this.#showLocations = true;
    return this;
  }

  showProjectionLines(){
    this.#showProjectionLines = true;
    return this;
  }

  /**
    * @param {HTMLCanvasElement | import('canvas').Canvas} canvas
    */
  withAdaptiveThresholding(canvas){
    this.#adaptiveThresholdingCanvas = canvas;
    return this;
  }

  tryDecode(){

    this.#tryDecode = true;
    return this;
  }

  /**
    * visualize the frame from the canvas
    * @param {HTMLCanvasElement | import('canvas').Canvas} canvasElement
    */
  visualizeFrame(canvasElement){

    const canvas = canvasElement.getContext("2d");
    // we will not try to decode the qr code, instead just process the image to greyscale and binary image
    const imageData = canvas.getImageData(0, 0, canvasElement.width, canvasElement.height);

    const image4Channel = Image4Channel.fromImageData(imageData);
    const greyScale = image4Channel.toGreyScale();
    const binarized = globalOtsuThreshold(greyScale);
    const adaptiveThresholdBinarized = createBinaryFilter(24, 8)(greyScale);

    if (this.#greyscaleCanvasElement){

      this.#greyscaleCanvasElement.width = canvasElement.width;
      this.#greyscaleCanvasElement.height = canvasElement.height;
      const greyscaleCanvas = this.#greyscaleCanvasElement.getContext("2d");

      const imageData = greyscaleCanvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
      imageData.data.set(greyScale.toImage4Channel().data);
      greyscaleCanvas.putImageData(imageData, 0, 0);
    }

    if (this.#binaryCanvasElement){
      this.#binaryCanvasElement.width = canvasElement.width;
      this.#binaryCanvasElement.height = canvasElement.height;

      const binaryCanvas = this.#binaryCanvasElement.getContext("2d");
      const imageData = binaryCanvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
      imageData.data.set(binarized.toImage4Channel().data);
      binaryCanvas.putImageData(imageData, 0, 0);
    }

    if (this.#adaptiveThresholdingCanvas) {

      this.#adaptiveThresholdingCanvas.width = canvasElement.width;
      this.#adaptiveThresholdingCanvas.height = canvasElement.height;

      const adaptiveBinarizeCanvas = this.#adaptiveThresholdingCanvas.getContext("2d");
      const imageData = adaptiveBinarizeCanvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
      imageData.data.set(adaptiveThresholdBinarized.toImage4Channel().data);
      adaptiveBinarizeCanvas.putImageData(imageData, 0, 0);
    }

    const qrLocations = locate(binarized);
    
    if(qrLocations){

      if(qrLocations[0].dimension > 191 || qrLocations[0].dimension <= 20){
	return;
      }
      // we know that jsqr returns 2 locations 
      const projectionMatrix = computeTransformationMatrix(qrLocations[0]);
      const sampler = generateSampler(projectionMatrix);

      const topLeftFinderPattern = qrLocations[0].topLeft;
      const topRightFinderPattern = qrLocations[0].topRight;
      const bottomLeftFinderPattern = qrLocations[0].bottomLeft;

      const sourceCorners = {
	topRightCorner: sampler(qrLocations[0].dimension, 0),
	topLeftCorner: sampler(0, 0),
	bottomLeftCorner: sampler(0, qrLocations[0].dimension),
	bottomRightCorner: sampler(qrLocations[0].dimension, qrLocations[0].dimension),
      };

      if (this.#showLocations && this.#greyscaleCanvasElement){
	const ctx = this.#greyscaleCanvasElement.getContext("2d");
	drawQRLocations(ctx, sourceCorners, topLeftFinderPattern, topRightFinderPattern, bottomLeftFinderPattern);
      }

      if (this.#showLocations && this.#binaryCanvasElement){
	const ctx = this.#binaryCanvasElement.getContext("2d");
	drawQRLocations(ctx, sourceCorners, topLeftFinderPattern, topRightFinderPattern, bottomLeftFinderPattern);
      }

      if (this.#showProjectionLines && this.#greyscaleCanvasElement){
	const ctx = this.#greyscaleCanvasElement.getContext("2d");

	// draw the sampling lines 
	const dimension = qrLocations[0].dimension;

	ctx.save();
	for (let i = -5; i < dimension + 5; i++){
	  const x1 = sampler(i + 0.5, -5);
	  const x2 = sampler(i + 0.5, dimension + 5);
	  ctx.beginPath();
	  ctx.moveTo(x1.x, x1.y);
	  ctx.lineTo(x2.x, x2.y);
	  ctx.strokeStyle = "green";
	  ctx.lineWidth = 1;
	  ctx.stroke();

	  const y1 = sampler(-5, i + 0.5);
	  const y2 = sampler(dimension + 5, i + 0.5)
	  ctx.beginPath();
	  ctx.moveTo(y1.x, y1.y);
	  ctx.lineTo(y2.x, y2.y);
	  ctx.lineStroke = "green";
	  ctx.lineWidth = 1;
	  ctx.stroke();
	  
	}
	ctx.restore();
      }

      if (this.#tryDecode && this.#greyscaleCanvasElement){

	const squareMatrix = binarized.sampleToSquareMatrix(sampler, qrLocations[0].dimension);
	const version = readVersion(squareMatrix);
	const formatInfo = readFormatInformation(squareMatrix);

	const ctx = this.#greyscaleCanvasElement.getContext("2d");
	ctx.save();
	
	ctx.fillStyle = "red";
	ctx.font = "bold 12px Arial";
	ctx.textAllign = "left";
	ctx.textBaseine = "middle";
	if (formatInfo){

	  const errLevel = formatInfo.metadata.error_correction_level.level;
	  const maskPattern = formatInfo.maskNumber;

	  ctx.fillText(`ErrorCorrectionLevel: ${errLevel}`, sourceCorners.topLeftCorner.x, sourceCorners.topLeftCorner.y + 12);
	  ctx.fillText(`Mask: ${maskPattern}`, sourceCorners.topLeftCorner.x, sourceCorners.topLeftCorner.y + 24);
	}
	ctx.fillText(`Version: ${version? version.value: "null"}`, sourceCorners.topLeftCorner.x, sourceCorners.topLeftCorner.y);

	if (formatInfo !== null && version !== null){
	  const pipeline = standardDecodingPipeline2(squareMatrix);
	  
	  if (pipeline.value !== null){
	    ctx.fillText(`message: ${pipeline.value.text}`, sourceCorners.topLeftCorner.x, sourceCorners.topLeftCorner.y + 36);
	  }
	}
      }
    }
  }
}

function drawQRLocations(ctx, sourceCorners, topLeftFinderPattern, topRightFinderPattern, bottomLeftFinderPattern){

  const topLeft = sourceCorners.topLeftCorner;
  const topRight = sourceCorners.topRightCorner;
  const bottomLeft = sourceCorners.bottomLeftCorner;
  const bottomRight = sourceCorners.bottomRightCorner;

  ctx.save()
  ctx.beginPath();
  ctx.strokeStyle = 'blue';
  ctx.lineWidth = 2;
  ctx.moveTo(topLeft.x, topLeft.y);
  ctx.lineTo(topRight.x, topRight.y);
  ctx.lineTo(bottomRight.x, bottomRight.y);
  ctx.lineTo(bottomLeft.x, bottomLeft.y);
  ctx.lineTo(topLeft.x, topLeft.y);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(topLeftFinderPattern.x, topLeftFinderPattern.y, 5, 0, 2 * Math.PI);
  ctx.fillStyle = "red";
  ctx.fill();

  ctx.beginPath();
  ctx.arc(topRightFinderPattern.x, topRightFinderPattern.y, 5, 0, 2 * Math.PI);
  ctx.fillStyle = "red";
  ctx.fill();

  ctx.beginPath();
  ctx.arc(bottomLeftFinderPattern.x, bottomLeftFinderPattern.y, 5, 0, 2 * Math.PI);
  ctx.fillStyle = "red";
  ctx.fill();

  ctx.restore();
}
